import { Injectable } from "@angular/core";
import { ActivityActorEvent } from "../../model/activityActor-event";
import { DayPilot, DayPilotSchedulerComponent } from "daypilot-pro-angular";
import { BusEnum, activityAbbreviations } from "../../common/enums";
import { ToastrService } from 'ngx-toastr';
import { Actor } from "../../model/actor";
import { Activity } from "../../model/activity";
import { StateMachineEventEnum } from "../../model/stateMachineEvent";
import { WorkShiftService } from '../../services/workShift.service';
import { Availability } from "../../model/availability";
import { ConfirmDialog } from "../../confirmation/confirm.dialog";
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LiveFlightEvent } from "../../model/liveflight-event";
import { AllowedEvents, SchedulerUpperService } from "./scheduler-upper-service";
import { WorkShift } from "../../model/workShift";

@Injectable()
export class ContextMenuHelper {

  confirmModalRef: MatDialogRef<ConfirmDialog>;

  contextMenu: DayPilot.Menu = new DayPilot.Menu({
    items: [
      { text: '', disabled: false, onClick: (args) => { } }
    ]
  });

  constructor(
    private readonly toastr: ToastrService,
    private readonly schedulerUpperService: SchedulerUpperService,
    private readonly workShiftService: WorkShiftService,
    private readonly dialog: MatDialog) { }

  addStatusItemsToContextMenu(event: ActivityActorEvent, allowedEvents: AllowedEvents) {
    let statusItem = this.contextMenu.items.find(x => x.text == "Status");
    statusItem.items = [];

    let states = allowedEvents.ToList();

    states.forEach((state: string) => {
      if (state != "Cancel" && state != "UnAssign" && state != "ReAssign" && state != "Final") {
        let item = activityAbbreviations.find(x => x.state == state);
        statusItem.items.push({ text: item.translated, disabled: false, onClick: (args) => { this.hideMenu(); this.changeStatus(event, state); } });
      }
    });
  }

  addTransferItemsToContextMenu(event: ActivityActorEvent, type: string) {
    if (type == BusEnum.Transfer) {

      let abb = activityAbbreviations.find(x => x.state == event.activityActor.state);

      let etaLabel: string = "Ankomst (min)";
      this.contextMenu.items.push(
        {
          text: etaLabel, disabled: !abb.eta, onClick: (args) => { }
        });

      let etaItem = this.contextMenu.items.find(x => x.text == etaLabel);
      if (etaItem) {
        etaItem.items = [];
        let eta = ["5", "10", "15", "20", "25"];
        eta.forEach((min: string) => {
          etaItem.items.push({ text: min, disabled: !abb.eta, onClick: (args) => { this.changeEta(event, min) } });
        });
      }
    }
  }

  addCopyItemsToContextMenu(event: ActivityActorEvent, args: any) {
    let copyItem = this.contextMenu.items.find(x => x.text == "Kopiera");

    if (copyItem.disabled) {
      return;
    }
    copyItem.items = [];

    let workShiftsAir = this.workShiftService.getWorkShiftsForAir();
    workShiftsAir.forEach((workShift: WorkShift) => {

      if (workShift.userMappingID === event.activityActor.userMappingID) { return; }

      // Check if driver is av
        let availability = this.findAvailabilityForDate(workShift.availabilities, args.e.data.start, args.e.data.end);
        if (availability !== undefined){
          copyItem.items.push({
            text: workShift.name,
            disabled: false,
            onClick: (args) => {
              this.hideMenu();
              this.copyActivityInternal(event.activityActor.actor, event.activityActor.id, availability.id);
            }
          });
        }
    });

    if (copyItem.items.length === 0) {
      console.log("No available drivers to copy to!");

      copyItem.items.push({
        text: "inga tillgängliga förare",
        disabled: true,
        onClick: (args) => { }
      });
    }
  }

  isAirAdminMiscOrTransfer(type: string) {
    return type == BusEnum.Air || type == BusEnum.Admin || type == BusEnum.Misc || type == BusEnum.Transfer;
  }

  findAvailabilityForDate(availabilities: Availability[], start: DayPilot.Date, end: DayPilot.Date): Availability {
    let foundItems = availabilities.filter(a => this.toDayPilotDate(a.start) <= start && this.toDayPilotDate(a.end) >= end);

    if (foundItems.length == 0) return undefined;

    if (foundItems.length == 1) return foundItems[0];
  }
  private toDayPilotDate(date: Date) {
    return new DayPilot.Date(date, true);
  }

  async createContextMenu(args: DayPilot.SchedulerEventRightClickArgs, liveFlightEvents: LiveFlightEvent[], schedulerUpper: DayPilotSchedulerComponent) {
  
    let activityActorEvent: ActivityActorEvent = args.e.data;

    let allowedEvents = await this.schedulerUpperService.getAllowedEvents(args.e.data.activityActor);

    let actor: Actor = activityActorEvent.activityActor.actor;

    if (this.isAirAdminMiscOrTransfer(actor.type)) {

      this.contextMenu = this.createContextMenuForAirAdminMiscOrTransfer(activityActorEvent, allowedEvents, actor, actor.type, liveFlightEvents, schedulerUpper);
      this.addTransferItemsToContextMenu(activityActorEvent, args.e.data.activityActor.actor.type);
      this.addCopyItemsToContextMenu(activityActorEvent, args);

    }
    else if (args.e.data.activityActor.actor.type == BusEnum.Land) {
      this.contextMenu = this.createContextMenuForLandside(allowedEvents, activityActorEvent, allowedEvents);
    }

    this.addStatusItemsToContextMenu(activityActorEvent, allowedEvents);
    this.showMenu();
  };

  createContextMenuForAirAdminMiscOrTransfer(event: ActivityActorEvent, allowedEvents: AllowedEvents, actor: Actor, type: string, liveFlightEvents: LiveFlightEvent[], schedulerUpper: DayPilotSchedulerComponent) {

    let activity: Activity = event.activityActor;
    let lfEvent = liveFlightEvents.find(x => x.id == event.activityActor.actor.liveFlightID);
    let canAck = lfEvent?.liveFlight.acknowledge;
    let canCopy = event.activityActor.state != StateMachineEventEnum.Cancel && event.activityActor.state != StateMachineEventEnum.Finish && event.activityActor.state != StateMachineEventEnum.Final;
    let canAssign = event.activityActor.state == StateMachineEventEnum.Planned;
    let canConfirmNoShow = event.activityActor.state == StateMachineEventEnum.NoShow && event.activityActor.status === 'ACTIVE';

    let menuItems = [
      { text: "Bekräfta förändring", disabled: !canAck, onClick: (args) => { this.hideMenu(); this.ackLiveFlight(event) } },
      { text: "Tilldela", disabled: !canAssign, onClick: (args) => { this.hideMenu(); this.changeStatus(event, "Assign") } },
      {
        text: "Kopiera",
        disabled: !canCopy || type == BusEnum.Transfer, onClick: (args) => { }
      },
      { text: "Status", disabled: allowedEvents.ToList().length === 0 || activity.state === StateMachineEventEnum.Finish || activity.state === StateMachineEventEnum.NoShow, onClick: (args) => { } },
      { text: "Avboka", disabled: !allowedEvents.UnAssign(), onClick: (args) => { this.hideMenu(); this.unassignActivity(actor, activity, schedulerUpper); } },
      { text: "Slutförd", disabled: !allowedEvents.Final(), onClick: (args) => { this.hideMenu(); this.changeStatus(event, "Final") } },
      {
        text: "Avbryt", disabled: !allowedEvents.Cancel(), onClick: (args) => {

          this.confirmModalRef = this.dialog.open(ConfirmDialog, {
            disableClose: false, width: "400px", autoFocus: false,
            closeOnNavigation: false, data:
            {
              title: "Bekräfta",
              text: "Vill du verkligen avbryta uppdraget?"
            }
          });

          this.confirmModalRef.afterClosed().subscribe(result => {
            if (result) {

              this.cancelActivity(actor, activity);
            }
          });
        }
      }
    ]

    if (canConfirmNoShow) {
      menuItems.push({ text: "Bekräfta BOM", disabled: false, onClick: (args) => { this.hideMenu(); this.confirmNoShow(event) } })
    }

    return new DayPilot.Menu({
      items: menuItems
    });
  }

  createContextMenuForLandside(reply: any, event: ActivityActorEvent, allowedEvents: AllowedEvents) {
    return new DayPilot.Menu({
      items: [
        { text: "Status", disabled: reply.length == 0, onClick: (args) => { } },
        { text: "Slutförd", disabled: !allowedEvents.Final(), onClick: (args) => this.changeStatus(event, "Final") }
      ]
    });

  }

  showMenu() {
    this.contextMenu.show();
  }

  hideMenu() {
    this.contextMenu.hide();
  }

  showError(message: string) {
    this.toastr.error(message);
  }

  showInfo(message: string) {
    this.toastr.info(message);
  }

  /// service calls

  changeStatus(event: ActivityActorEvent, status: string) {
     this.schedulerUpperService.updateState(status, event.activityActor.actor, event.activityActor).then();
  }

  async changeEta(event: ActivityActorEvent, min: string) {
    await this.schedulerUpperService.updateTransferEta(event.activityActor.actorID, min);
  }

  async ackLiveFlight(event: ActivityActorEvent) {
    await this.schedulerUpperService.ackFlight(event.activityActor.actor.liveFlightID);
  }

  async copyActivityInternal(actor: Actor, sourceActivityId: string, targetAvailabilityId: string) {
    await this.schedulerUpperService.copyActivity(actor, sourceActivityId, targetAvailabilityId);
  }

  async cancelActivity(actor: Actor, activity: Activity) {
    await this.schedulerUpperService.cancelActivity(actor, activity);
  }

  async confirmNoShow(event: ActivityActorEvent) {
    await this.schedulerUpperService.confirmNoShow(event.activityActor.id);
  }

  async unassignActivity(actor: Actor, activity: Activity, schedulerUpper: DayPilotSchedulerComponent) {
    // find the activity that is to be unassigned and remove it from daypilot collection to avoid waiting for signalr and prevent user to click on it
    // again before signalr comes back.
    let existing = schedulerUpper.control.events.find(f => f.id() === activity.id);
    if (existing) {
      schedulerUpper.control.events.remove(existing);
    }

    await this.schedulerUpperService.unassingActivity(actor, activity)
      .then(result => {
        if (!result) {
          // something went wrong - add the activity again
          schedulerUpper.control.events.add(existing);
        }
      }, (error) => {
        // something went wrong - add the activity again
        schedulerUpper.control.events.add(existing);
      });
  }

  /// service calls end

}

